import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { PermissionsProvider } from "./hooks/usePermissions";
import Layout from "./components/layout/Layout";
import Login from "./pages/Login";
import Register from "./pages/Register";
import Onboarding from "./pages/Onboarding";
import { ForgotPassword, ResetPassword } from "./pages/PasswordPages";
import AuthCallback from "./pages/AuthCallback";
import Dashboard from "./pages/Dashboard";
import ACMSLiveMonitor from "./pages/ACMSLiveMonitor";
import LiveMonitor from "./pages/LiveMonitor";
import GraphicalDashboard from "./pages/GraphicalDashboard";
import PanelsManagement from "./pages/PanelsManagement";
import DeviceManagement from "./pages/DeviceManagement";
import SiteManagement from "./pages/SiteManagement";
import AlertManagement from "./pages/AlertManagement";
import UserManagement from "./pages/UserManagement";
import AuditLog from "./pages/AuditLog";
import Billing from "./pages/Billing";
import FloorPlans from "./pages/FloorPlans";
import MoreMenu from "./pages/MoreMenu";
import DeviceHistory from "./pages/DeviceHistory";
import HydrantPage from "./pages/HydrantPage";
import SprinklerPage from "./pages/SprinklerPage";
import TankPage from "./pages/TankPage";
import DetectionPage from "./pages/DetectionPage";
import VentilationPage from "./pages/VentilationPage";
import RolesPermissions from "./pages/RolesPermissions";
import HardwareGateways from "./pages/HardwareGateways";
import SystemSettings from "./pages/SystemSettings";
import ControllerSettings from "./pages/ControllerSettings";
import CertificateManagement from "./pages/CertificateManagement";
import SubscriptionRevenue from "./pages/SubscriptionRevenue";
import AssetRegistry from "./pages/AssetRegistry";
import RegistryRoute from "./pages/RegistryRoute";
import { REGISTRY_DEFS } from "./pages/registrySchemas";
import LiftsModule from "./pages/LiftsModule";
import AdminSubscriptions from "./pages/admin/AdminSubscriptions";
import AdminModuleCatalogue from "./pages/admin/AdminModuleCatalogue";
import AdminInvoices from "./pages/admin/AdminInvoices";
import AdminOrganizations from "./pages/admin/AdminOrganizations";
import IAQMonitor from "./pages/bms/IAQMonitor";
import EnergyDashboard from "./pages/bms/EnergyDashboard";
import WorkOrders from "./pages/bms/WorkOrders";
import AlertDispatch from "./pages/AlertDispatch";
import OnboardingWizard from "./pages/OnboardingWizard";
import GatewayCommands from "./pages/GatewayCommands";
import ControlManagement from "./pages/ControlManagement";
import AutomationRules from "./pages/AutomationRules";
import AlertSettings from "./pages/AlertSettings";
import FirstRunWizard from "./pages/FirstRunWizard";
import Quotes from "./pages/Quotes";
import PublicQuoteView from "./pages/PublicQuoteView";
import NfcTagOpen from "./pages/NfcTagOpen";
import GRCompliance from "./pages/GRCompliance";
import Compliance from "./pages/Compliance";
import AddressablePanel from "./pages/AddressablePanel";
import FCCDisplay from "./pages/FCCDisplay";
import {
  ReportsPage, HydrantPage as HydrantPlaceholder, SprinklerPage as SprinklerPlaceholder,
  TankPage as TankPlaceholder, DetectionPage as DetectionPlaceholder, VentilationPage as VentilationPlaceholder,
  RolesPage, GatewayPage, CertificatePage, ControllerPage, SettingsPage,
} from "./pages/OtherPages";

function AppRouter() {
  const loc = useLocation();
  if (loc.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  // If already authenticated and visiting an auth route, clear stored token so the form is reachable for testing/account switching.
  const isAuthRoute = ["/register", "/forgot-password", "/reset-password"].some((p) => loc.pathname.startsWith(p));
  if (isAuthRoute && localStorage.getItem("f360_token")) {
    // intentional: don't auto-redirect — let the user explicitly create another account or reset.
  }
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/onboarding" element={<Onboarding />} />
      <Route path="/setup" element={<OnboardingWizard />} />
      <Route path="/first-run" element={<FirstRunWizard />} />
      <Route path="/q/:token" element={<PublicQuoteView />} />
      <Route path="/t/:code" element={<NfcTagOpen />} />
      <Route path="/fcc" element={<FCCDisplay />} />
      <Route element={<Layout />}>
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/acms" element={<ACMSLiveMonitor />} />
        <Route path="/live-monitor" element={<LiveMonitor />} />
        <Route path="/graphical" element={<GraphicalDashboard />} />
        <Route path="/hydrant" element={<HydrantPage />} />
        <Route path="/sprinkler" element={<SprinklerPage />} />
        <Route path="/tank" element={<TankPage />} />
        <Route path="/detection" element={<DetectionPage />} />
        <Route path="/ventilation" element={<VentilationPage />} />
        <Route path="/panels" element={<PanelsManagement />} />
        <Route path="/devices" element={<DeviceManagement />} />
        <Route path="/devices/:device_id/history" element={<DeviceHistory />} />
        <Route path="/sites" element={<SiteManagement />} />
        <Route path="/floors" element={<FloorPlans />} />
        <Route path="/alerts" element={<AlertManagement />} />
        <Route path="/reports" element={<ReportsPage />} />
        <Route path="/audit" element={<AuditLog />} />
        <Route path="/billing" element={<Billing />} />
        <Route path="/quotes" element={<Quotes />} />
        <Route path="/gr-compliance" element={<GRCompliance />} />
        <Route path="/compliance" element={<Compliance />} />
        <Route path="/addressable-panel" element={<AddressablePanel />} />
        <Route path="/subscription" element={<SubscriptionRevenue />} />
        <Route path="/pumps" element={<AssetRegistry assetType="pump" title={REGISTRY_DEFS.pump.title} icon={REGISTRY_DEFS.pump.icon} accentColor={REGISTRY_DEFS.pump.accentColor} schema={REGISTRY_DEFS.pump.schema} />} />
        <Route path="/registry/:kind" element={<RegistryRoute />} />
        <Route path="/lifts-module" element={<LiftsModule />} />
        <Route path="/users" element={<UserManagement />} />
        <Route path="/menu" element={<MoreMenu />} />
        <Route path="/roles" element={<RolesPermissions />} />
        <Route path="/gateways" element={<HardwareGateways />} />
        <Route path="/gateway" element={<HardwareGateways />} />
        <Route path="/certificate" element={<CertificateManagement />} />
        <Route path="/controller" element={<ControllerSettings />} />
        <Route path="/settings" element={<SystemSettings />} />
        <Route path="/admin/subscriptions" element={<AdminSubscriptions />} />
        <Route path="/admin/module-catalogue" element={<AdminModuleCatalogue />} />
        <Route path="/admin/invoices" element={<AdminInvoices />} />
        <Route path="/admin/organisations" element={<AdminOrganizations />} />
        <Route path="/iaq-monitor" element={<IAQMonitor />} />
        <Route path="/energy-dashboard" element={<EnergyDashboard />} />
        <Route path="/work-orders" element={<WorkOrders />} />
        <Route path="/alert-dispatch" element={<AlertDispatch />} />
        <Route path="/gateway-commands" element={<GatewayCommands />} />
        <Route path="/control-management" element={<ControlManagement />} />
        <Route path="/automation" element={<AutomationRules />} />
        <Route path="/alerts/settings" element={<AlertSettings />} />
        {/* Route aliases (common mistyped paths) */}
        <Route path="/fire-tank" element={<Navigate to="/tank" replace />} />
        <Route path="/certificates" element={<Navigate to="/certificate" replace />} />
      </Route>
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function AppWithPermissions() {
  const { user } = useAuth();
  return (
    <PermissionsProvider user={user}>
      <AppRouter />
      <Toaster theme="dark" position="top-right" richColors />
    </PermissionsProvider>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppWithPermissions />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
